import cn from 'classnames';
import {Alert} from '@material-ui/lab';
import {IconButton} from '@material-ui/core';
import {useDispatch, useSelector} from 'react-redux';
import {FormattedMessage, useIntl} from 'react-intl';
import {Visibility, VisibilityOff} from '@material-ui/icons';
import React, {useEffect, useState, useMemo, useRef, useCallback} from 'react';
import {Field, FieldProps, Form, useFormikContext} from 'formik';

import {RootState} from '../../../store';
import {projectFetch} from '../../../store/project';
import {Prompts} from '../../../types/prompts/Prompts';
import {Project} from '../../../types/project/Project';
import {BaseModel} from '../../../types/job/BaseModel';
import {DynamicForm} from '../../DynamicForm';
import {useProjectList} from '../../ProjectList/useProjectList';
import {DEPLOY_PANEL_SETTINGS} from '../../../config/constants';
import {StudioSelect} from '../../../base-components/StudioSelect';
import {Points} from '../../../types/deployment/StreamingDeviceResponse';
import {StudioTextField} from '../../../base-components/StudioTextField';
import {ApplicationDetails} from '../ApplicationDetails/ApplicationDetails';
import {ApplicationInfo} from '../../../types/deployment/ApplicationsResponse';
import {fetchApplications} from '../../../store/prodDeployments/create-deployment';
import {AnalyticsParametersAnswer} from '../../../types/prompts/AnalyticsParameters';
import {DeviceGroupInfo} from '../../../types/deployment/DeploymentDeviceGroup';
import {useDeploy, setStreamingDevices} from '../../TestDeployView/useDeploy';
import {AddStreamingDeviceForm} from '../../TestDeployView/AddStreamingDeviceForm/AddStreamingDeviceForm';
import {assertResponseBody} from '../../../api/request';
import {getDeviceList} from '../../../api/device/DeviceList';
import TargetDeviceGroupTable from './TargetDeviceGroupTable';
import {StartUponDeploymentNodeType} from './CreateDeployment';

import './CreateDeployment.scss';
import {TestParametersAccordion} from '../../TestDeployView/DeployConfigPanel/TestDeployParameters';

export type ProdDeployFormValues = {
  name: string;
  deviceGroupId: string;
  nodeId: string;
  projectId: string;
  applicationId: string;
  description: string | null;
  confThreshold: number | null;
  ddaDataMaxSize: number | null;
  samplingFrequency: number | null;
  nonMaximumSuppression: number | null;
  regions?: Points[];
  lines?: Points[];
  environment?: {[key: string]: string} | {};
  analyticsParameters?: AnalyticsParametersAnswer;
};

type CreateDeploymentFormType = {
  projectData: Project;
  isDeploying: boolean;
  isProjectContext: boolean;
  selectApplication: Function;
  environmentParameters: Record<string, string>;
  analyticParametersPrompts: Prompts[];
  getCompatibleNodes: () => Array<DeviceGroupInfo> | undefined | null;
  getCompatibleDeviceGroups: () => Array<DeviceGroupInfo> | undefined | null;
  startUponDeploymentNodes: StartUponDeploymentNodeType[];
  setStartUponDeploymentNodes: (data: StartUponDeploymentNodeType[]) => void;
};

export default function CreateDeploymentForm({
  projectData,
  isDeploying,
  isProjectContext,
  selectApplication,
  environmentParameters,
  analyticParametersPrompts,
  getCompatibleNodes,
  getCompatibleDeviceGroups,
  startUponDeploymentNodes,
  setStartUponDeploymentNodes,
}: CreateDeploymentFormType) {
  const PROJECT_LIST_PAGE_SIZE = useMemo(() => 5, []);

  const intl = useIntl();
  const formRef = useRef<HTMLFormElement>(null);
  const dispatch = useDispatch();
  const [, deployDispatch] = useDeploy();
  const {
    setFieldValue,
    values: {projectId, applicationId, nodeId, deviceGroupId},
  } = useFormikContext<ProdDeployFormValues>();
  const [revealAppBar, setRevealAppBar] = useState(false);
  const [devicesChanged, setDeviceChanged] = useState(true);

  const [
    isTargetDeviceGroupTableModalOpen,
    setIsTargetDeviceGroupTableModalOpen,
  ] = useState(false);

  useEffect(() => {
    if (projectId) {
      dispatch(projectFetch(projectId));
      dispatch(fetchApplications(projectId));
    }
  }, [projectId, dispatch, setFieldValue]);

  useEffect(() => {
    if (devicesChanged) {
      const fetchStreamingDevicesList = async () => {
        const devices = await assertResponseBody(getDeviceList());
        if (devices) {
          deployDispatch(setStreamingDevices(devices));
        }
      };

      fetchStreamingDevicesList();
      setDeviceChanged(false);
    }
  }, [deployDispatch, devicesChanged]);

  useEffect(() => {
    setFieldValue('environment', environmentParameters);
  }, [environmentParameters, setFieldValue]);

  const applications = useSelector(
    (state: RootState) => state.createDeployment.applications
  );
  const {data: projectListData, totalCount, loadMore: loadMoreProjects} = useProjectList(
    PROJECT_LIST_PAGE_SIZE,
    '',
    'name',
    'asc'
  );

  const projectList = useMemo(
    () =>
      projectListData?.flatMap(projectResponse => projectResponse?.body?.projects) || [],
    [projectListData]
  );
  const projectOptions = useMemo(
    () =>
      projectList.reduce<Record<string, string>>((options, newOption) => {
        options[newOption.id] = newOption.name;

        return options;
      }, {}),
    [projectList]
  );

  const projectOptionsOrder: string[] | undefined = useMemo(() => {
    let order;

    if (projectId && projectData) {
      // ensure current project is available in options
      projectOptions[projectId] = projectData.name || '';
      order = [
        projectId,
        ...projectList
          .filter(project => project.id !== projectId)
          .map(project => project.id),
      ];
    }

    return order;
  }, [projectId, projectData, projectList, projectOptions]);

  const [supportedNodes, supportedModels] = useMemo(() => {
    const supportedNodes = (getCompatibleNodes()?.flatMap(n => n.nodes) || []).filter(
      node => node?.status === 'ONLINE'
    );
    const supportedModels = new Set<BaseModel>();

    supportedNodes
      .flatMap(node => node?.baseModels)
      .forEach(node => node && supportedModels.add(node));

    return [supportedNodes, supportedModels];
  }, [getCompatibleNodes]);

  const compatibleDeviceGroupsOptions = useMemo(() => {
    const deviceGroups = (getCompatibleDeviceGroups() || []).filter(
      d => d?.status !== 'ALL_OFFLINE'
    );
    return deviceGroups?.reduce<Record<string, string>>((options, deviceGroup) => {
      if (deviceGroup) {
        options[deviceGroup.id] = deviceGroup.name;
      }

      return options;
    }, {});
  }, [getCompatibleDeviceGroups]);

  const disabledApplications = useMemo(
    () =>
      applications.reduce<Record<string, boolean>>(
        (applicationsToDisable, application) => {
          applicationsToDisable[application.id] = !application.compatibleRuntimes
            ?.map(app => app.type)
            .some(appType => appType !== 'ONNX_RUNTIME_WEB');

          return applicationsToDisable;
        },
        {}
      ),
    [applications]
  );

  const applicationOptions = useMemo(
    () =>
      applications.reduce<Record<string, string>>((options, application) => {
        options[application.id] = application.name;

        return options;
      }, {}),
    [applications]
  );

  const nodesOptions = useMemo(
    () =>
      supportedNodes?.reduce<Record<string, string>>((options, node) => {
        if (node) {
          options[node.id] = node.name;
        }

        return options;
      }, {}),
    [supportedNodes]
  );

  let selectedApplication: ApplicationInfo | null | undefined = useMemo(
    () => applications.find(app => app.id === applicationId),
    [applications, applicationId]
  );

  const showEnvironmentParameters = useMemo(
    () => Object.keys(environmentParameters).length > 0,
    [environmentParameters]
  );

  const showAnalyticsParameters = useMemo(() => analyticParametersPrompts.length > 0, [
    analyticParametersPrompts,
  ]);

  const supportedModelsAlertList = useMemo(() => {
    let supportList = '';
    const modelsList = Array.from(supportedModels);
    const filteredYoloModels = modelsList.filter(model => model.includes('YOLO'));

    if (filteredYoloModels.length) {
      const yoloVersions = Array.from(
        new Set(filteredYoloModels.map(model => model.match(/V(\d+)/)?.[1]))
      ).join('/');

      supportList = `${supportList}\n${intl.formatMessage(
        {
          id: 'createDeploy.modelsCompatibilityAlert.yolo',
        },
        {
          yoloVersions,
        }
      )}`;
    }

    if (
      modelsList.find(
        supportedModel => ['RESNET50', 'SWIFTNET'].indexOf(supportedModel) !== -1
      )
    ) {
      supportList = `${supportList}\n${intl.formatMessage({
        id: 'createDeploy.modelsCompatibilityAlert.resnet50',
      })}`;
    }

    if (modelsList.includes('PYACT_APP')) {
      supportList = `${supportList}\n${intl.formatMessage({
        id: 'createDeploy.modelsCompatibilityAlert.pyActApp',
      })}`;
    }

    if (modelsList.includes('PYTHON_APP')) {
      supportList = `${supportList}\n${intl.formatMessage({
        id: 'createDeploy.modelsCompatibilityAlert.pythonApp',
      })}`;
    }

    return supportList ? `\n${supportList}` : supportList;
  }, [supportedModels, intl]);

  useEffect(() => {
    const analyticsParameters: AnalyticsParametersAnswer = analyticParametersPrompts.map(
      ({key, prefilledValue}) => ({key, value: prefilledValue})
    );

    setFieldValue('analyticsParameters', analyticsParameters);
  }, [setFieldValue, analyticParametersPrompts]);

  const analyticsParametersOnBlur = useCallback(
    (formValues: Record<string, any>) => {
      const analyticsParameters: AnalyticsParametersAnswer = Object.keys(formValues).map(
        key => ({
          key,
          value: formValues[key],
        })
      );

      setFieldValue('analyticsParameters', analyticsParameters);
    },
    [setFieldValue]
  );

  return (
    <>
      <Form
        noValidate
        ref={formRef}
        id="prod-deploy-form"
        className="create-prod-deploy__form"
      >
        <div className="create-prod-deploy__row">
          {!isProjectContext && (
            <Field name="projectId">
              {({field, meta}: FieldProps<ProdDeployFormValues['projectId']>) => (
                <StudioSelect
                  className={cn(
                    'create-prod-deploy__field',
                    'create-prod-deploy__field__right_space',
                    projectId
                      ? 'create-prod-deploy__select__selected'
                      : 'create-prod-deploy__select__unselected'
                  )}
                  dataTestId="create-prod-deploy-project"
                  label={intl.formatMessage({id: 'createDeploy.project'})}
                  options={projectOptions}
                  order={projectOptionsOrder}
                  onLoadMore={loadMoreProjects}
                  hasMore={totalCount > projectList.length}
                  placeholder={intl.formatMessage({
                    id: 'createDeploy.selectProject',
                  })}
                  disablePlaceholder
                  SelectProps={{
                    ...field,
                    onChange: e => {
                      field.onChange(e);
                      setFieldValue('applicationId', '');
                      setFieldValue('nodeId', '');
                      setFieldValue('deviceGroupId', '');
                    },
                  }}
                  error={Boolean(meta.error && meta.touched)}
                  helperText={meta.error && meta.touched ? meta.error : ''}
                  disabled={isDeploying}
                  required
                />
              )}
            </Field>
          )}
          <Field name="applicationId">
            {({field, meta}: FieldProps<ProdDeployFormValues['applicationId']>) => (
              <>
                <StudioSelect
                  className={cn(
                    'create-prod-deploy__field',
                    applicationId
                      ? 'create-prod-deploy__select__selected'
                      : 'create-prod-deploy__select__unselected'
                  )}
                  dataTestId="create-prod-deploy-application"
                  label={intl.formatMessage({id: 'createDeploy.application'})}
                  options={applicationOptions}
                  placeholder={intl.formatMessage({
                    id: 'createDeploy.selectApplication',
                  })}
                  disablePlaceholder
                  SelectProps={{
                    ...field,
                    onChange: (e: React.ChangeEvent<any>) => {
                      field.onChange(e);
                      const newApplicationId = e.target.value;
                      selectedApplication = applications.find(
                        app => app.id === newApplicationId
                      );
                      selectApplication(selectedApplication);
                      const newSupportedNodes = getCompatibleNodes();
                      if (!newSupportedNodes?.find(node => node.id === nodeId)) {
                        setFieldValue('nodeId', '');
                      }
                      const newDeviceGroups = getCompatibleDeviceGroups();
                      if (
                        !newDeviceGroups?.find(
                          deviceGroup => deviceGroup.id === deviceGroupId
                        )
                      ) {
                        setFieldValue('deviceGroupId', '');
                      }
                    },
                  }}
                  error={Boolean(meta.error && meta.touched)}
                  helperText={meta.error && meta.touched ? meta.error : ''}
                  disabled={isDeploying}
                  required
                  disabledOptions={disabledApplications}
                />
                <IconButton
                  className={cn(
                    'create-prod-deploy__reveal',
                    field.value && !meta.error && 'create-prod-deploy__reveal--visible'
                  )}
                  onClick={() => setRevealAppBar(reveal => !reveal)}
                  disabled={isDeploying}
                >
                  {revealAppBar ? <VisibilityOff /> : <Visibility />}
                </IconButton>
                {selectedApplication && (
                  <Alert severity="warning" className="create-prod-deploy__alert">
                    {supportedModels.size ? (
                      <>
                        <FormattedMessage
                          tagName="span"
                          id="createDeploy.compatibilityAlert.Some"
                        />
                        <span>{supportedModelsAlertList}</span>
                      </>
                    ) : (
                      <FormattedMessage
                        tagName="span"
                        id="createDeploy.compatibilityAlert.None"
                      />
                    )}
                  </Alert>
                )}
              </>
            )}
          </Field>
        </div>
        <div className="create-prod-deploy__app-info">
          <Field name="applicationId">
            {({field, meta}: FieldProps<ProdDeployFormValues['applicationId']>) =>
              Boolean(field.value && !meta.error && revealAppBar) && (
                <ApplicationDetails
                  className="create-prod-deploy__details"
                  applicationInfo={applications.find(
                    application => application.id === field.value
                  )}
                  description={projectData?.description}
                />
              )
            }
          </Field>
        </div>
        <div className="create-prod-deploy__row start">
          <Field name="name">
            {({field, meta}: FieldProps<ProdDeployFormValues['name']>) => (
              <StudioTextField
                id="create-deploy-deployment-name"
                className="create-prod-deploy__field create-prod-deploy__field__right_space"
                label={intl.formatMessage({id: 'createDeploy.deploymentName'})}
                placeholder={intl.formatMessage({id: 'createDeploy.enterName'})}
                InputProps={{...field}}
                error={Boolean(meta.error && meta.touched)}
                helperText={meta.error && meta.touched ? meta.error : ''}
                disabled={isDeploying}
                required
                inputProps={{
                  maxLength: DEPLOY_PANEL_SETTINGS.name.maxLength,
                }}
              />
            )}
          </Field>
          <Field name="description">
            {({field, meta}: FieldProps<ProdDeployFormValues['description']>) => (
              <StudioTextField
                id="create-deploy-deployment-descripition"
                className={cn(
                  'create-prod-deploy__field',
                  'create-prod-deploy__field--large'
                )}
                label={intl.formatMessage({id: 'createDeploy.description'})}
                placeholder={intl.formatMessage({id: 'createDeploy.enterDesc'})}
                InputProps={{...field}}
                error={Boolean(meta.error && meta.touched)}
                helperText={meta.error && meta.touched ? meta.error : ''}
                disabled={isDeploying}
                multiline={DEPLOY_PANEL_SETTINGS.description.multiline}
                inputProps={{
                  className: DEPLOY_PANEL_SETTINGS.description.className,
                  maxLength: DEPLOY_PANEL_SETTINGS.description.maxLength,
                }}
              />
            )}
          </Field>
        </div>
        {applicationId && selectedApplication && (
          <>
            <div className="create-prod-deploy__row">
              <Field name="nodeId">
                {({field, meta}: FieldProps<ProdDeployFormValues['nodeId']>) => (
                  <StudioSelect
                    className={cn(
                      'create-prod-deploy__field',
                      field.value.length > 0
                        ? 'create-prod-deploy__select__selected'
                        : 'create-prod-deploy__select__unselected'
                    )}
                    dataTestId="create-prod-deploy-gsp"
                    label={intl.formatMessage({id: 'createDeploy.gspTarget'})}
                    placeholder={intl.formatMessage({
                      id: 'createDeploy.selectTarget',
                    })}
                    disablePlaceholder
                    options={nodesOptions}
                    SelectProps={{
                      ...field,
                      onChange: (e: React.ChangeEvent<any>) => {
                        field.onChange(e);
                        setFieldValue('deviceGroupId', '', false);
                      },
                    }}
                    error={Boolean(meta.error && meta.touched)}
                    helperText={meta.error && meta.touched ? meta.error : ''}
                    disabled={isDeploying}
                    required
                    placeholderClassName="create-prod-deploy__select__placeholder"
                  />
                )}
              </Field>
              <div className="create-prod-deploy__or">
                <FormattedMessage id="createDeploy.gspTargetOr" />
              </div>
              <Field name="deviceGroupId">
                {({field, meta}: FieldProps<ProdDeployFormValues['deviceGroupId']>) => (
                  <StudioSelect
                    className={cn(
                      'create-prod-deploy__field',
                      field.value.length > 0
                        ? 'create-prod-deploy__select__selected'
                        : 'create-prod-deploy__select__unselected'
                    )}
                    dataTestId="create-prod-deploy-gsp-device-group"
                    label={intl.formatMessage({
                      id: 'createDeploy.gspTargetDeviceGroup',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'createDeploy.selectTarget',
                    })}
                    disablePlaceholder
                    options={compatibleDeviceGroupsOptions}
                    SelectProps={{
                      ...field,
                      onChange: (e: React.ChangeEvent<any>) => {
                        field.onChange(e);
                        setFieldValue('nodeId', '', false);
                      },
                    }}
                    error={Boolean(meta.error && meta.touched)}
                    helperText={meta.error && meta.touched ? meta.error : ''}
                    disabled={isDeploying}
                    required
                    placeholderClassName="create-prod-deploy__select__placeholder"
                  />
                )}
              </Field>
            </div>
            <div className="create-prod-deploy__column">
              <TargetDeviceGroupTable
                deviceGroupId={deviceGroupId || nodeId}
                deviceGroupName={
                  compatibleDeviceGroupsOptions[deviceGroupId] || nodesOptions[nodeId]
                }
                isDeploying={isDeploying}
                isTargetDeviceGroupTableModalOpen={isTargetDeviceGroupTableModalOpen}
                setIsTargetDeviceGroupTableModalOpen={
                  setIsTargetDeviceGroupTableModalOpen
                }
                startUponDeploymentNodes={startUponDeploymentNodes}
                setStartUponDeploymentNodes={setStartUponDeploymentNodes}
                showCheckBoxes={false}
                show={deviceGroupId.length > 0 || nodeId.length > 0}
              />
            </div>
          </>
        )}
        {selectedApplication && selectedApplication.modelPurpose !== 'Classification' && (
          <TestParametersAccordion
            title={intl.formatMessage({id: 'createDeploy.deploymentSettings'})}
            className="dplmt-config__accordion"
            detailsClassName="deploy-settings__body_container"
            defaultExpand={true}
          >
            <div className="create-prod-deploy__sub">
              <p className="create-prod-deploy__sub__title">
                {intl.formatMessage({id: 'createDeploy.inferenceSettings'})}
              </p>
              <div className="create-prod-deploy__row">
                <Field name="nonMaximumSuppression">
                  {({
                    field,
                    meta,
                  }: FieldProps<ProdDeployFormValues['nonMaximumSuppression']>) => (
                    <StudioTextField
                      id="create-deploy-deployment-non-maximum-suppression"
                      className="create-prod-deploy__field"
                      label={intl.formatMessage({id: 'createDeploy.nmsThreshold'})}
                      placeholder={intl.formatMessage({
                        id: 'createDeploy.enterThreshold',
                      })}
                      type="number"
                      inputProps={{
                        step: 0.1,
                      }}
                      InputProps={{...field}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={isDeploying}
                      tooltip={intl.formatMessage({
                        id: 'createDeploy.nmsThreshold.tooltip',
                      })}
                    />
                  )}
                </Field>
                <Field name="confThreshold">
                  {({field, meta}: FieldProps<ProdDeployFormValues['confThreshold']>) => (
                    <StudioTextField
                      id="create-deploy-deployment-conf-threshold"
                      className="create-prod-deploy__field"
                      label={intl.formatMessage({id: 'createDeploy.confThreshold'})}
                      placeholder={intl.formatMessage({
                        id: 'createDeploy.enterThreshold',
                      })}
                      type="number"
                      inputProps={{
                        step: 0.1,
                      }}
                      InputProps={{...field}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={isDeploying}
                      tooltip={intl.formatMessage({
                        id: 'createDeploy.confThreshold.tooltip',
                      })}
                    />
                  )}
                </Field>
              </div>
            </div>
            <div className="create-prod-deploy__sub">
              <p className="create-prod-deploy__sub__title">
                {intl.formatMessage({id: 'createDeploy.mlopsSettings'})}
              </p>
              <div className="create-prod-deploy__row">
                <Field name="samplingFrequency">
                  {({
                    field,
                    meta,
                  }: FieldProps<ProdDeployFormValues['samplingFrequency']>) => (
                    <StudioTextField
                      required
                      disabled={isDeploying}
                      id="create-deploy-deployment-sampling-frequency"
                      className="create-prod-deploy__field"
                      label={intl.formatMessage({
                        id: 'createDeploy.samplingInterval',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'createDeploy.frameInterval',
                      })}
                      type="number"
                      InputProps={{...field}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      tooltip={intl.formatMessage({
                        id: 'createDeploy.samplingInterval.tooltip',
                      })}
                    />
                  )}
                </Field>
                <Field name="ddaDataMaxSize">
                  {({
                    field,
                    meta,
                  }: FieldProps<ProdDeployFormValues['ddaDataMaxSize']>) => (
                    <StudioTextField
                      id="create-deploy-deployment-dda-data-max-size"
                      className="create-prod-deploy__field"
                      label={intl.formatMessage({id: 'createDeploy.ddaDataMaxSize'})}
                      placeholder={intl.formatMessage({id: 'createDeploy.numFrames'})}
                      type="number"
                      InputProps={{...field}}
                      error={Boolean(meta.error && meta.touched)}
                      helperText={meta.error && meta.touched ? meta.error : ''}
                      disabled={isDeploying}
                      tooltip={intl.formatMessage({
                        id: 'createDeploy.ddaDataMaxSize.tooltip',
                      })}
                    />
                  )}
                </Field>
              </div>
            </div>
          </TestParametersAccordion>
        )}
        {showEnvironmentParameters && (
          <TestParametersAccordion
            title={intl.formatMessage({id: 'environmentParameters.title'})}
            className="dplmt-config__accordion"
            detailsClassName="deploy-settings__body_container"
            defaultExpand={true}
          >
            <section>
              <div className="create-prod-deploy__columns">
                {Object.keys(environmentParameters).map(envVar => (
                  <div
                    key={envVar}
                    className="create-prod-deploy__column create-prod-deploy__environment_item"
                  >
                    <Field name={'environment.' + envVar}>
                      {({field}: FieldProps<ProdDeployFormValues['environment']>) => (
                        <StudioTextField
                          id="create-deploy-deployment-environment"
                          className="create-prod-deploy__field"
                          label={envVar}
                          placeholder={environmentParameters[envVar]}
                          InputProps={{...field}}
                          disabled={isDeploying}
                        />
                      )}
                    </Field>
                  </div>
                ))}
              </div>
            </section>
          </TestParametersAccordion>
        )}
      </Form>
      {showAnalyticsParameters && (
        <TestParametersAccordion
          title={intl.formatMessage({id: 'analyticsParameters.title'})}
          className="dplmt-config__accordion"
          detailsClassName="deploy-settings__body_container"
          defaultExpand={true}
        >
          <section>
            <div className="create-prod-deploy__columns">
              <DynamicForm
                onBlur={analyticsParametersOnBlur}
                prompts={analyticParametersPrompts}
                className="analytics-parameters__form"
                key={`form-${selectedApplication?.id}`}
              />
            </div>
          </section>
        </TestParametersAccordion>
      )}

      <AddStreamingDeviceForm
        onAddedAsset={(): void => {
          setDeviceChanged(true);
        }}
        enableSelect
      />
    </>
  );
}
